import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { apiFetch } from '../lib/api';
import { fetchStats, type GameStats } from '../lib/solves';
import { maybeRunAlwaysDownload } from '../lib/alwaysDownload';

export interface AuthUser {
  id: string;
  email: string;
  displayName: string;
  emailVerified: boolean;
  timezone: string;
  plan: string;
  isPremium: boolean;
  /// Account-tied hint balance. Synced across web + iOS — same balance
  /// you spend on hexponents.com is the one you spend on the app. Free
  /// users get 3 on signup + 5/month (lazy-granted). Out-of-hints
  /// users buy a 25-pack for $0.99.
  hintsBalance: number;
}

export interface OAuthProviders {
  google: boolean;
  apple: boolean;
}

export type AuthResult =
  | { ok: true }
  | { ok: false; error: string; issues?: unknown };

interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  oauthProviders: OAuthProviders;
  stats: GameStats | null;
  signup: (email: string, password: string, displayName: string) => Promise<AuthResult>;
  /// `identifier` accepts email or displayName — the server resolves
  /// either form to the same user record.
  login: (identifier: string, password: string) => Promise<AuthResult>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  setStats: (stats: GameStats | null) => void;
  refreshStats: () => Promise<void>;
  /// Optimistic-update setter so the hint UI can flip immediately on
  /// spend, without waiting for the next /me round-trip.
  setHintsBalance: (n: number) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

async function readError(res: Response): Promise<{ error: string; issues?: unknown }> {
  try {
    const body = await res.json();
    return { error: String(body.error ?? 'request_failed'), issues: body.issues };
  } catch {
    return { error: 'request_failed' };
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [oauthProviders, setOauthProviders] = useState<OAuthProviders>({ google: false, apple: false });
  const [stats, setStats] = useState<GameStats | null>(null);

  const refreshStats = useCallback(async () => {
    const s = await fetchStats();
    setStats(s);
  }, []);

  // Hydrate on mount: ask the server who we are. /auth/me is HEAD-light
  // and returns the same shape signup/login do.
  // Wrapped in try/catch so a non-JSON response (e.g. a dev server's
  // SPA fallback when no API is mounted) doesn't trigger an unhandled
  // rejection that leaves the UI stuck.
  const refresh = useCallback(async () => {
    try {
      const res = await apiFetch('/auth/me');
      if (res.ok) {
        const body = await res.json();
        setUser(body.user as AuthUser);
      } else {
        setUser(null);
      }
    } catch {
      setUser(null);
    }
  }, []);

  useEffect(() => {
    refresh().finally(() => setLoading(false));
    apiFetch('/auth/oauth/providers')
      .then(r => (r.ok ? r.json() : null))
      .then(d => {
        if (d && typeof d.google === 'boolean' && typeof d.apple === 'boolean') {
          setOauthProviders({ google: d.google, apple: d.apple });
        }
      })
      .catch(() => { /* leave defaults */ });
  }, [refresh]);

  // Stats follow the auth state: hydrate on sign-in, clear on sign-out.
  useEffect(() => {
    if (!user) { setStats(null); return; }
    refreshStats();
    // 2026-05-12: auto-archive top-up for premium users who have
    // "Always download" toggled on. Module-level guard inside
    // maybeRunAlwaysDownload collapses repeat calls across route swaps.
    if (user.isPremium) maybeRunAlwaysDownload();
  }, [user, refreshStats]);

  const signup = useCallback<AuthContextValue['signup']>(async (email, password, displayName) => {
    const res = await apiFetch('/auth/signup', {
      method: 'POST',
      body: JSON.stringify({ email, password, displayName }),
    });
    if (res.ok) {
      const body = await res.json();
      setUser(body.user as AuthUser);
      return { ok: true };
    }
    return { ok: false, ...(await readError(res)) };
  }, []);

  const login = useCallback<AuthContextValue['login']>(async (identifier, password) => {
    const res = await apiFetch('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ identifier, password }),
    });
    if (res.ok) {
      const body = await res.json();
      setUser(body.user as AuthUser);
      return { ok: true };
    }
    return { ok: false, ...(await readError(res)) };
  }, []);

  const logout = useCallback<AuthContextValue['logout']>(async () => {
    await apiFetch('/auth/logout', { method: 'POST' });
    setUser(null);
  }, []);

  const setHintsBalance = useCallback<AuthContextValue['setHintsBalance']>((n) => {
    setUser(prev => prev ? { ...prev, hintsBalance: n } : prev);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ user, loading, oauthProviders, stats, signup, login, logout, refresh, setStats, refreshStats, setHintsBalance }),
    [user, loading, oauthProviders, stats, signup, login, logout, refresh, refreshStats, setHintsBalance],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const v = useContext(AuthContext);
  if (!v) throw new Error('useAuth must be used inside <AuthProvider>');
  return v;
}
